import { QueryClient } from 'react-query';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
// @ts-ignore
import Keycloak from 'Keycloak';
import { createAuthHeader, withTokenUpdate } from './authUtils';
import { stringifyUrl } from 'query-string';
import { ResponseError } from './errors';

export const queryClient = new QueryClient();

const appName = process.env.REACT_APP_APP_NAME;
const apiKey = process.env.REACT_APP_CONTACT_FORM_API_GATEWAY_API_KEY;

let keycloak: Keycloak = null;

const ResponseType = Object.freeze({
  JSON: 'json',
  BLOB: 'blob',
});

const xCallerRequestHeaders: Map<string, string | undefined> = new Map([['X-Caller', appName]]);

const apiGatewayRequestHeaders: Map<string, string | undefined> = new Map([['x-api-key', apiKey]]);

const defaultRequestHeaders: Map<string, string | undefined> = new Map([
  ['Content-Type', 'application/json'],
  ...Array.from(xCallerRequestHeaders.entries()),
  ...Array.from(apiGatewayRequestHeaders.entries()),
]);

export const initAxios = (): void => {
  defaultRequestHeaders.forEach((value, header) => {
    axios.defaults.headers.common[header] = value;
  });
  axios.interceptors.request.use(
    (axiosRequestConfig) => {
      axiosRequestConfig.headers.common = enrichWithAuthHeader(axiosRequestConfig.headers.common);
      return axiosRequestConfig;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};

function makeRequest<T>(
  url: string,
  method: string,
  data: Nullable<T> = {} as T,
  headers: {} = {},
  responseType: string = ResponseType.JSON,
) {
  const options = {
    url,
    method,
    headers,
    data,
    responseType,
  };

  return axios(options as AxiosRequestConfig)
    .then((response: AxiosResponse) => {
      return Promise.resolve(response.data);
    })
    .catch((error: AxiosError) => {
      if (error.response) {
        const { url = '' } = error.request as AxiosRequestConfig;
        const { status, data } = error.response as AxiosResponse;
        if (status >= 201 && status <= 299) {
          console.log(
            `Received a 2XX response with following error: ${error.message}. Returning null response content instead...`,
          );
          return Promise.resolve(null);
        }
        throw ResponseError.fromResponseData(data, status, url);
      } else {
        const errorMessage = error.message;
        console.error(errorMessage);
        throw new ResponseError(errorMessage);
      }
    });
}

export async function get<T>(url: string, query?: {}, headers: {} = {}): Promise<T> {
  const requestUrl = stringifyUrl(
    {
      url,
      query: query || {},
    },
    {
      skipNull: true,
    },
  );

  return makeRequest(requestUrl, 'GET', null, headers);
}

export async function post<T, R>(url: string, data?: T, headers?: {}): Promise<R> {
  return makeRequest(url, 'POST', data, headers);
}

export async function put<T, R>(url: string, data?: T, headers?: {}): Promise<R> {
  return makeRequest(url, 'PUT', data, headers);
}

export function authenticated() {
  return {
    get(url: string, query?: {}, headers?: {}) {
      return makeAuthenticatedRequest(() => get(url, query, enrichWithAuthHeader(headers)));
    },
    post<T>(url: string, data: T, headers?: {}) {
      return makeAuthenticatedRequest(() => post(url, data, enrichWithAuthHeader(headers)));
    },
    put<T>(url: string, data: T, headers?: {}) {
      return makeAuthenticatedRequest(() => put(url, data, enrichWithAuthHeader(headers)));
    },
  };
}

function makeAuthenticatedRequest(action: () => Promise<any>): Promise<any> {
  if (!keycloak) {
    return Promise.reject(new ResponseError('Authentication context not available'));
  }

  return withTokenUpdate(keycloak, action);
}

function enrichWithAuthHeader(headers: {} = {}) {
  const authHeaders = keycloak && keycloak.token ? createAuthHeader(keycloak.token) : {};
  return {
    ...headers,
    ...authHeaders,
  };
}

export function applyAuthContext(kc: Keycloak) {
  keycloak = kc;
}
